import {
  IconChevronDown,
  IconChevronRight,
  IconFileCertificate,
  IconFolder,
  IconFolderFilled,
  IconFolderOpen,
} from '@tabler/icons-react';
import { type CSSProperties, type KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './Compliance.module.css';
import type { CertTreeNode, CertTreeRow } from './certificate-view.js';

const KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowLeft',
  'Home',
  'End',
  'Enter',
  ' ',
]);

/** Düğümün görünen adı: kök ve kategori dilden, ara düğüm ve tür veriden. */
export function useNodeLabel() {
  const { t } = useTranslation();
  return (node: CertTreeNode) => {
    if (node.kind === 'root') return t('vesselCertificates.tree.root');
    if (node.kind === 'category') {
      return t(`vesselCertificates.categories.${node.category}`, {
        defaultValue: node.category ?? '',
      });
    }
    return node.label ?? '';
  };
}

export interface CertificateTreeProps {
  rows: readonly CertTreeRow[];
  selectedId: string;
  focusedId: string | null;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onKey: (key: string, id: string) => void;
  onFocus: (id: string) => void;
  /** Klavye isteği sayacı: yalnız değişince odak satıra taşınır. */
  focusRequest?: number;
}

/**
 * "Certificate Tracking" ağacı (`gemi-sertifika-takibi.jpeg`): WAI-ARIA tree (düz liste,
 * `aria-level`; tek sekme durağı). Ok tuşları gezinir, → ve ← açar/kapatır, Enter ve boşluk seçer.
 */
export function CertificateTree({
  rows,
  selectedId,
  focusedId,
  onSelect,
  onToggle,
  onKey,
  onFocus,
  focusRequest = 0,
}: CertificateTreeProps) {
  const { t } = useTranslation();
  const label = useNodeLabel();
  const listRef = useRef<HTMLDivElement>(null);
  const tabStop =
    rows.find((r) => r.node.id === focusedId)?.node.id ??
    rows.find((r) => r.node.id === selectedId)?.node.id ??
    rows[0]?.node.id ??
    null;
  const tabStopRef = useRef(tabStop);
  tabStopRef.current = tabStop;
  const handled = useRef(focusRequest);
  useEffect(() => {
    if (focusRequest === handled.current) return;
    handled.current = focusRequest;
    if (!tabStopRef.current) return;
    const item = listRef.current?.querySelector<HTMLElement>(
      `[data-node-id="${CSS.escape(tabStopRef.current)}"]`,
    );
    if (item && item !== document.activeElement) item.focus();
  }, [focusRequest]);

  const keyDown = (event: KeyboardEvent<HTMLDivElement>, id: string) => {
    if (!KEYS.has(event.key)) return;
    event.preventDefault();
    onKey(event.key, id);
  };

  return (
    <div className={classes.tree}>
      <div
        role="tree"
        aria-label={t('vesselCertificates.tree.label')}
        className={classes.treeList}
        ref={listRef}
      >
        {rows.map((row) => {
          const { node } = row;
          const selected = node.id === selectedId;
          const name = label(node);
          const Icon =
            node.kind === 'type'
              ? IconFileCertificate
              : row.expanded
                ? node.kind === 'root'
                  ? IconFolderFilled
                  : IconFolderOpen
                : IconFolder;
          return (
            <div
              key={node.id}
              role="treeitem"
              aria-level={row.level}
              aria-setsize={row.setSize}
              aria-posinset={row.position}
              aria-selected={selected}
              aria-expanded={row.hasChildren ? row.expanded : undefined}
              tabIndex={node.id === tabStop ? 0 : -1}
              data-node-id={node.id}
              data-kind={node.kind}
              data-selected={selected || undefined}
              className={classes.treeItem}
              style={{ '--tree-level': row.level - 1 } as CSSProperties}
              onClick={(event) => {
                const toggle = (event.target as HTMLElement).closest('[data-toggle]');
                if (toggle && row.hasChildren) onToggle(node.id);
                else onSelect(node.id);
              }}
              onKeyDown={(event) => keyDown(event, node.id)}
              onFocus={() => onFocus(node.id)}
            >
              {Array.from({ length: row.level - 1 }, (_, rail) => rail).map((rail) => (
                <span
                  key={rail}
                  className={classes.treeRail}
                  style={{ '--rail': rail } as CSSProperties}
                  aria-hidden
                />
              ))}
              <span className={classes.treeToggle} data-toggle aria-hidden>
                {row.hasChildren ? (
                  row.expanded ? (
                    <IconChevronDown size={14} stroke={2} />
                  ) : (
                    <IconChevronRight size={14} stroke={2} />
                  )
                ) : null}
              </span>
              <span className={classes.treeIcon} aria-hidden>
                <Icon size={16} stroke={1.8} />
              </span>
              <span className={classes.treeName} title={name}>
                {name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
